import { Autocomplete, Box, Button, ButtonGroup, TextField, Typography } from '@mui/material'
import type { KeyNameType } from '@tunasong/music-lib'
import { getKeyNames, prettifyNote, scaleNoteNames } from '@tunasong/music-lib'
import type { NoteName, Scale, ScaleType } from '@tunasong/schemas'
import { useDimensions } from '@tunasong/ui-lib'
import type { FC } from 'react'

export interface ScaleSelectProps {
  className?: string
  value?: Partial<Scale> | null
  title?: string
  fullWidth?: boolean
  variant?: KeyNameType
  allowNone?: boolean
  onChange(scale: Scale | null): void
}

export const ScaleSelect: FC<ScaleSelectProps> = props => {
  const { value = null, allowNone = true, onChange, variant = 'common' } = props

  const { isSmallOrSmaller } = useDimensions()

  const handleScaleType = (type: string | null) => {
    const scale = type ? { root: value?.root ?? 'C', type: type as ScaleType } : null
    onChange(scale)
  }

  const handleRoot = (note?: NoteName) => {
    onChange(note ? { root: note, type: value?.type ?? 'major' } : null)
  }

  const keyNames = getKeyNames(variant)
  const buttonSx = { textTransform: 'none', p: 0, px: 1, borderColor: 'divider' }

  const showModes = variant === 'all' || variant === 'mode'

  return (
    <>
      <Box
        sx={{
          mt: 1,
          gap: 2,
          alignItems: isSmallOrSmaller ? 'flex-start' : 'center',
          display: 'flex',
          flexDirection: isSmallOrSmaller ? 'column' : 'row',
        }}
      >
        <Typography variant="subtitle2" sx={{ mr: 1 }}>
          {props.title ?? 'Root'}
        </Typography>
        <ButtonGroup variant="outlined" size="small" sx={{ flexWrap: 'wrap', flex: 1 }}>
          {allowNone ? (
            <Button
              sx={buttonSx}
              variant={value === null ? 'contained' : 'outlined'}
              color={value === null ? 'primary' : 'inherit'}
              onClick={() => handleScaleType(null)}
            >
              <i>None</i>
            </Button>
          ) : null}

          {scaleNoteNames.map(note => (
            <Button
              sx={{ textTransform: 'none', p: 0, borderColor: 'divider' }}
              key={note}
              variant={value?.root === note ? 'contained' : 'outlined'}
              color={value?.root === note ? 'primary' : 'inherit'}
              onClick={() => handleRoot(note)}
            >
              {prettifyNote(note)}
            </Button>
          ))}
        </ButtonGroup>

        <Box sx={{ minWidth: 200, position: 'relative' }}>
          <Autocomplete
            size="small"
            options={keyNames}
            value={value?.type ?? null}
            onChange={(_, newValue) => handleScaleType(newValue)}
            disabled={value === null}
            renderInput={params => <TextField {...params} label="Scale Type" variant="outlined" />}
            sx={{ mb: showModes ? 2 : 0 }}
          />
        </Box>
      </Box>
    </>
  )
}
